import React, { useState, useEffect } from 'react';
import { emailFiltersAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Plus, Pencil, Trash2, Filter, Play } from 'lucide-react';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const ACTION_LABELS = {
  move: 'Déplacer vers',
  mark_read: 'Marquer comme lu',
  mark_flagged: 'Marquer comme important',
  delete: 'Supprimer',
};

const FOLDER_OPTIONS = [
  { value: 'INBOX', label: 'Boîte de réception' },
  { value: 'Sent', label: 'Envoyés' },
  { value: 'Drafts', label: 'Brouillons' },
  { value: 'Trash', label: 'Corbeille' },
  { value: 'Spam', label: 'Spam' },
];

const defaultForm = {
  name: '',
  enabled: true,
  from_contains: '',
  from_exact: '',
  to_contains: '',
  subject_contains: '',
  has_attachments: null,
  action: 'move',
  target_folder: '',
  order: 0,
};

export default function EmailFilterManager() {
  const [filters, setFilters] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadFilters(); }, []);

  const loadFilters = async () => {
    try {
      const res = await emailFiltersAPI.getFilters();
      setFilters(res.data.filters);
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    if (form.action === 'move' && !form.target_folder) { toast.error('Dossier cible requis'); return; }
    setLoading(true);
    try {
      const data = { ...form, has_attachments: form.has_attachments === 'true' ? true : form.has_attachments === 'false' ? false : null };
      if (editing) {
        await emailFiltersAPI.updateFilter(editing.id, data);
        toast.success('Filtre mis à jour');
      } else {
        await emailFiltersAPI.createFilter(data);
        toast.success('Filtre créé');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...defaultForm });
      loadFilters();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setLoading(false); }
  };

  const handleEdit = (f) => {
    setEditing(f);
    setForm({
      ...f,
      has_attachments: f.has_attachments === true ? 'true' : f.has_attachments === false ? 'false' : null,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await emailFiltersAPI.deleteFilter(id);
      toast.success('Filtre supprimé');
      loadFilters();
    } catch { toast.error('Erreur de suppression'); }
  };

  const handleApplyAll = async () => {
    try {
      const res = await emailFiltersAPI.applyFilters();
      toast.success(res.data.message);
    } catch { toast.error('Erreur lors de l\'application'); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const getConditionsSummary = (f) => {
    const parts = [];
    if (f.from_contains) parts.push(`De contient "${f.from_contains}"`);
    if (f.from_exact) parts.push(`De = "${f.from_exact}"`);
    if (f.to_contains) parts.push(`A contient "${f.to_contains}"`);
    if (f.subject_contains) parts.push(`Sujet contient "${f.subject_contains}"`);
    if (f.has_attachments === true) parts.push('Avec pièces jointes');
    if (f.has_attachments === false) parts.push('Sans pièces jointes');
    return parts.length > 0 ? parts.join(' + ') : 'Aucune condition';
  };

  return (
    <div className="space-y-4" data-testid="filter-manager">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Filtres automatiques</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleApplyAll} data-testid="apply-filters-btn">
            <Play className="w-4 h-4 mr-2" /> Appliquer aux emails existants
          </Button>
          <Button onClick={openNew} size="sm" data-testid="new-filter-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau filtre
          </Button>
        </div>
      </div>

      {filters.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucun filtre. Les filtres s'appliquent automatiquement aux nouveaux emails lors de la synchronisation.</p>
      ) : (
        <div className="grid gap-3">
          {filters.map((f) => (
            <Card key={f.id} className={!f.enabled ? 'opacity-60' : ''} data-testid={`filter-card-${f.id}`}>
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Filter className="w-3 h-3" />
                  {f.name}
                  {!f.enabled && <Badge variant="secondary" className="text-xs">Désactivé</Badge>}
                </CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(f)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(f.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-2 px-4">
                <p className="text-xs text-muted-foreground">{getConditionsSummary(f)}</p>
                <p className="text-xs mt-1">
                  <Badge variant="outline" className="text-xs">
                    {ACTION_LABELS[f.action]}{f.action === 'move' && f.target_folder ? ` : ${f.target_folder}` : ''}
                  </Badge>
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="filter-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le filtre' : 'Nouveau filtre'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du filtre</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Newsletters" data-testid="filter-name-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(val) => setForm(p => ({ ...p, enabled: val }))} />
              <Label>Activé</Label>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Conditions</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Expéditeur contient</Label>
                  <Input value={form.from_contains} onChange={(e) => setForm(p => ({ ...p, from_contains: e.target.value }))} placeholder="ex: newsletter" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Expéditeur exact</Label>
                  <Input value={form.from_exact} onChange={(e) => setForm(p => ({ ...p, from_exact: e.target.value }))} placeholder="ex: noreply@site.com" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Destinataire contient</Label>
                  <Input value={form.to_contains} onChange={(e) => setForm(p => ({ ...p, to_contains: e.target.value }))} placeholder="ex: support" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Sujet contient</Label>
                  <Input value={form.subject_contains} onChange={(e) => setForm(p => ({ ...p, subject_contains: e.target.value }))} placeholder="ex: facture" className="h-8 text-sm" />
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-xs">Pièces jointes</Label>
                <Select value={form.has_attachments ?? 'any'} onValueChange={(v) => setForm(p => ({ ...p, has_attachments: v === 'any' ? null : v }))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Peu importe</SelectItem>
                    <SelectItem value="true">Avec pièces jointes</SelectItem>
                    <SelectItem value="false">Sans pièces jointes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Action</p>
              <Select value={form.action} onValueChange={(v) => setForm(p => ({ ...p, action: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="filter-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="move">Déplacer vers un dossier</SelectItem>
                  <SelectItem value="mark_read">Marquer comme lu</SelectItem>
                  <SelectItem value="mark_flagged">Marquer comme important</SelectItem>
                  <SelectItem value="delete">Supprimer (Corbeille)</SelectItem>
                </SelectContent>
              </Select>
              {form.action === 'move' && (
                <div className="mt-2">
                  <Label className="text-xs">Dossier cible</Label>
                  <Select value={form.target_folder} onValueChange={(v) => setForm(p => ({ ...p, target_folder: v }))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Choisir un dossier" /></SelectTrigger>
                    <SelectContent>
                      {FOLDER_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={loading} data-testid="filter-save-btn">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
